import { FC, memo, useCallback, useMemo, useState } from 'react';
import { spotifyActions } from '../../../../store/slices/spotify';
import { AddSongToLibraryButton } from '../../../Actions/AddSongToLibrary';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { uiActions } from '../../../../store/slices/ui';
import { TrackActionsWrapper } from '../../../Actions/TrackActions';
import { ArtistActionsWrapper } from '../../../Actions/ArtistActions';
import { usePlayback } from '../../../../contexts/PlaybackContext';
import { useComposerIdSet } from '../../../../utils/useComposerIdSet';
import { ComposerAwareLink } from '../../../shared/ComposerAwareLink';
import { Tooltip } from '../../../Tooltip';
import { AddToLibrary, AddedToLibrary } from '../../../Icons';
import type { LibraryRecording } from '../../../../interfaces/recording';
import { yourLibraryActions } from '../../../../store/slices/yourLibrary';
import { Dropdown } from 'antd';
import { useCascadeSelections } from '../../../../hooks/useCascadeSelections';
import { CascadeMenu } from '../../../Actions/CascadeMenu';
import {
  deriveQueuePointer,
  extractQueueItemMetadata,
  spotifyTrackIdFromUri,
} from '../../../../lib/playback/queueDisplay';
import NavigableLink from '../../../shared/NavigableLink';

const ArrowDown = (
  <svg
    width={16}
    height={16}
    data-encore-id='icon'
    role='img'
    aria-hidden='true'
    viewBox='0 0 16 16'
    className='Svg-sc-ytk21e-0 dYnaPI'
  >
    <path d='M.47 4.97a.75.75 0 0 1 1.06 0L8 11.44l6.47-6.47a.75.75 0 1 1 1.06 1.06L8 13.56.47 6.03a.75.75 0 0 1 0-1.06z'></path>
  </svg>
);

const ArrowUp = (
  <svg
    width={16}
    height={16}
    data-encore-id='icon'
    role='img'
    aria-hidden='true'
    viewBox='0 0 16 16'
    className='Svg-sc-ytk21e-0 dYnaPI'
  >
    <path d='M15.53 11.03a.75.75 0 0 1-1.06 1.06L8 4.56 1.53 11.03a.75.75 0 1 1-1.06-1.06L8 2.44l7.53 7.53a.75.75 0 0 1 0 1.06z'></path>
  </svg>
);

const SongDetails: FC<{ isMobile?: boolean }> = memo((props) => {
  const dispatch = useAppDispatch();

  const { playerState, queueSession, queueItems } = usePlayback();
  const current_track = playerState?.track_window.current_track ?? null;
  const isLiked = useAppSelector((state) => state.spotify.liked);
  const detailsOpen = useAppSelector((state) => !state.ui.detailsCollapsed);
  const composerIds = useComposerIdSet();

  const pointer = useMemo(() => deriveQueuePointer(queueSession, queueItems), [queueItems, queueSession]);
  const currentQueueItem = pointer.item;
  const queueMetadata = useMemo(() => extractQueueItemMetadata(currentQueueItem), [currentQueueItem]);
  const queueTrackId = useMemo(
    () => spotifyTrackIdFromUri(currentQueueItem?.trackUri),
    [currentQueueItem?.trackUri]
  );
  const recordingId = queueMetadata.recordingId;
  const myRecordings = useAppSelector((state) => state.yourLibrary.myRecordings);
  const userId = useAppSelector((state) => state.auth.user?.id ?? null);
  const [cascadeOpen, setCascadeOpen] = useState(false);
  const [isApplyingCascade, setIsApplyingCascade] = useState(false);

  const albumArt =
    current_track?.album.images && current_track.album.images.length > 0
      ? current_track.album.images[0]?.url ?? null
      : null;
  const displayTitle = queueMetadata.title ?? current_track?.name ?? '—';
  const queueArtistsText = queueMetadata.artists;
  const hasSpotifyTrack = Boolean(current_track);
  const addToLibraryId = current_track?.id ?? queueTrackId ?? null;

  const albumId = useMemo(() => {
    const album = current_track?.album as (Spotify.Album & { id?: string | null }) | undefined;
    if (!album) return null;

    const explicitId =
      typeof album.id === 'string' && album.id.trim().length > 0 ? album.id.trim() : null;
    if (explicitId) return explicitId;

    if (typeof album.uri === 'string') {
      const segments = album.uri.split(':');
      const fallback = segments[segments.length - 1]?.trim();
      if (fallback) return fallback;
    }

    return null;
  }, [current_track]);

  const handleToggle = () => {
    if (!current_track) return;
    dispatch(spotifyActions.setLiked({ liked: !isLiked }));
  };

  const playbackRecording = useMemo<LibraryRecording | null>(() => {
    if (!recordingId) return null;
    const recordingName =
      queueMetadata.recordingName ??
      queueMetadata.albumName ??
      displayTitle ??
      current_track?.name ??
      recordingId;
    const performerName =
      queueMetadata.performerName ??
      queueArtistsText ??
      (current_track?.artists?.map((artist) => artist.name).join(', ') || 'Unknown Performer');
    const workName =
      queueMetadata.workName ??
      queueMetadata.recordingName ??
      recordingName;
    const composerName =
      queueMetadata.composerName ??
      (current_track?.artists?.[0]?.name ?? '');
    const resolvedAlbumName =
      queueMetadata.albumName ??
      current_track?.album?.name ??
      recordingName;
    const resolvedAlbumId = albumId ?? recordingId;
    const resolvedUri =
      currentQueueItem?.trackUri ??
      current_track?.uri ??
      (queueTrackId ? `spotify:track:${queueTrackId}` : `cmf:recording:${recordingId}`);

    return {
      id: recordingId,
      uri: resolvedUri,
      type: 'recording',
      name: recordingName,
      performerName,
      workName,
      composerName,
      composerId: queueMetadata.composerId ?? undefined,
      year: queueMetadata.releaseYear ? String(queueMetadata.releaseYear) : undefined,
      imageUrl: albumArt ?? undefined,
      workId: queueMetadata.workId ?? undefined,
      albumId: resolvedAlbumId,
      albumName: resolvedAlbumName,
      albumImageUrl: albumArt ?? undefined,
      savedAt: undefined,
      labels: undefined,
      trackCount: queueMetadata.trackCount ?? undefined,
      notes: undefined,
      workCatalogLabel: queueMetadata.catalogKey ?? undefined,
      workCatalogNumberNormalized: queueMetadata.catalogNumberNormalized ?? undefined,
      spotifyTrackId: queueTrackId ?? undefined,
      spotifyUri: current_track?.uri ?? (queueTrackId ? `spotify:track:${queueTrackId}` : undefined),
    };
  }, [
    albumArt,
    albumId,
    currentQueueItem?.trackUri,
    current_track?.artists,
    current_track?.album?.name,
    current_track?.name,
    current_track?.uri,
    queueArtistsText,
    queueMetadata.albumName,
    queueMetadata.catalogKey,
    queueMetadata.catalogNumberNormalized,
    queueMetadata.composerId,
    queueMetadata.composerName,
    queueMetadata.performerName,
    queueMetadata.recordingName,
    queueMetadata.releaseYear,
    queueMetadata.trackCount,
    queueMetadata.workId,
    queueMetadata.workName,
    recordingId,
    displayTitle,
    queueTrackId,
  ]);

  const isRecordingSaved = useMemo(() => {
    if (!recordingId) return false;
    return myRecordings.some((recording) => recording.id === recordingId);
  }, [myRecordings, recordingId]);

  const recordingCascadeMode = isRecordingSaved ? 'remove' : 'save';
  const recordingCascadeOverrides = useMemo(() => ({ recording: true as const }), []);
  const {
    scopes: recordingCascadeScopes,
    selections: recordingCascadeSelections,
    setSelection: setRecordingCascadeSelection,
    resetSelections: resetRecordingCascadeSelections,
  } = useCascadeSelections({
    entityType: 'recording',
    mode: recordingCascadeMode,
    overrides: recordingCascadeOverrides,
  });

  const ensureUser = useCallback(() => {
    if (!userId) {
      dispatch(uiActions.openLoginTooltip());
      return false;
    }
    return true;
  }, [dispatch, userId]);

  const handleRecordingCascadeApply = useCallback(async () => {
    if (!playbackRecording) return;
    setIsApplyingCascade(true);
    try {
      const cascades = {
        tracks: recordingCascadeSelections.tracks ?? true,
      };
      if (recordingCascadeMode === 'save') {
        await dispatch(
          yourLibraryActions.saveRecordingWithCascade({
            recordingId: playbackRecording.id,
            workId: playbackRecording.workId,
            cascades,
          })
        ).unwrap();
      } else {
        await dispatch(
          yourLibraryActions.removeRecordingWithCascade({
            recordingId: playbackRecording.id,
            cascades,
          })
        ).unwrap();
      }
      void dispatch(yourLibraryActions.fetchMyRecordings());
      setCascadeOpen(false);
      resetRecordingCascadeSelections();
    } catch (error) {
      // errors handled by thunks
    } finally {
      setIsApplyingCascade(false);
    }
  }, [
    dispatch,
    playbackRecording,
    recordingCascadeMode,
    recordingCascadeSelections.tracks,
    resetRecordingCascadeSelections,
  ]);

  const cascadeLabels = useMemo(() => {
    if (recordingCascadeMode === 'save') {
      return {
        recording: 'Save recording',
        tracks: 'Save tracks',
      } as const;
    }
    return {
      recording: 'Remove recording',
      tracks: 'Remove tracks',
    } as const;
  }, [recordingCascadeMode]);

  const renderArtists = () => {
    if (current_track) {
      const artists = current_track.artists.slice(0, 3);
      return artists.map((artist, index) => (
        <span key={artist.uri}>
          <ArtistActionsWrapper artist={artist} trigger={['contextMenu']}>
            <ComposerAwareLink artist={artist} composerIds={composerIds}>
              {artist.name}
            </ComposerAwareLink>
          </ArtistActionsWrapper>
          {index < artists.length - 1 && ', '}
        </span>
      ));
    }

    return queueArtistsText ?? null;
  };

  const coverNode = (
    <div className='playing-cover-container'>
      {albumArt ? (
        <img alt='Album Cover' className='album-cover' src={albumArt} />
      ) : (
        <div className='album-cover placeholder' />
      )}
      <button
        aria-label='Now playing view'
        className='playing-cover-details-button'
        onClick={() => {
          dispatch(uiActions.toggleDetails());
        }}
      >
        {detailsOpen ? ArrowDown : ArrowUp}
      </button>
    </div>
  );

  const coverContent = hasSpotifyTrack ? (
    <TrackActionsWrapper
      saved={isLiked}
      track={current_track!}
      trigger={['contextMenu']}
      onSavedToggle={handleToggle}
    >
      {coverNode}
    </TrackActionsWrapper>
  ) : (
    coverNode
  );

  const isTitleInteractive = hasSpotifyTrack && Boolean(albumId);
  const titleClassName = `text-white font-bold song-title${isTitleInteractive ? ' song-title--link' : ''}`;

  const titleNode = isTitleInteractive ? (
    <NavigableLink
      to={`/album/${albumId}`}
      className={titleClassName}
      title={displayTitle}
      onClick={(e) => e.stopPropagation()}
    >
      {displayTitle}
    </NavigableLink>
  ) : (
    <p className={titleClassName} title={displayTitle}>
      {displayTitle}
    </p>
  );

  const titleContent = hasSpotifyTrack ? (
    <TrackActionsWrapper
      saved={isLiked}
      track={current_track!}
      trigger={['contextMenu']}
      onSavedToggle={handleToggle}
    >
      {titleNode}
    </TrackActionsWrapper>
  ) : (
    titleNode
  );

  return (
    <div className='flex flex-row items-center playing-container'>
      <div style={{ marginRight: 15 }}>
        {coverContent}
      </div>
      <div className='flex items-center gap-1 min-w-0'>
        <div id='song-and-artist-name'>
          {titleContent}
          <span className='text-gray-200 song-artist' title={queueArtistsText ?? undefined}>
            {renderArtists()}
          </span>
        </div>

        {!props.isMobile && hasSpotifyTrack ? (
          playbackRecording ? (
            <Dropdown
              trigger={['click']}
              open={cascadeOpen}
              overlayClassName='recording-actions-dropdown'
              placement='topRight'
              dropdownRender={() => (
                <div className='cascade-menu-standalone'>
                  <CascadeMenu
                    entityType='recording'
                    mode={recordingCascadeMode}
                    scopes={recordingCascadeScopes}
                    selections={recordingCascadeSelections}
                    labels={cascadeLabels}
                    disabledScopes={{ recording: true }}
                    onToggle={setRecordingCascadeSelection}
                    applyLabel='Apply'
                    onApply={handleRecordingCascadeApply}
                    onCancel={() => {
                      resetRecordingCascadeSelections();
                      setCascadeOpen(false);
                    }}
                    applyDisabled={isApplyingCascade}
                  />
                </div>
              )}
              onOpenChange={(open) => {
                if (!open) {
                  setCascadeOpen(false);
                  resetRecordingCascadeSelections();
                  setIsApplyingCascade(false);
                }
              }}
            >
              <Tooltip
                title={
                  isRecordingSaved ? 'Remove recording from Your Library' : 'Add recording to Your Library'
                }
                open={cascadeOpen ? false : undefined}
              >
                <button
                  type='button'
                  className='actions'
                  aria-label='Manage recording library settings'
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!ensureUser()) return;
                    setCascadeOpen((previous) => !previous);
                  }}
                >
                  {isRecordingSaved ? (
                    <AddedToLibrary height={17} width={17} />
                  ) : (
                    <AddToLibrary height={17} width={17} />
                  )}
                </button>
              </Tooltip>
            </Dropdown>
          ) : addToLibraryId ? (
            <AddSongToLibraryButton
              size={17}
              isSaved={isLiked}
              id={addToLibraryId}
              onToggle={handleToggle}
            />
          ) : null
        ) : null}
      </div>
    </div>
  );
});

export default SongDetails;
